import React, { Component } from "react";
import { Link } from "react-router-dom";
import { AxiosResponse, AxiosError } from "axios";
import { postRequest } from "../../utils/httpRequest";

type AuthState = "forgot" | "sent";

interface State {
  email: string;
  errorMessage: string;
  emailSent: boolean;
  saving: boolean;
}

class ForgotPassword extends Component<{}, State> {
  constructor(props) {
    super(props);

    this.state = {
      email: "",
      errorMessage: "",
      emailSent: false,
      saving: false,
    };
  }

  componentDidMount() {
    document.body.classList.add("auth-login-page-active");
  }

  componentWillUnmount() {
    document.body.classList.remove("auth-login-page-active");
  }

  validateEmail = (email) => {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return true;
    }

    return false;
  };

  handleChange = (e) => {
    this.setState({ email: e.target.value, errorMessage: "" });
  };

  onSubmit = (e) => {
    e.preventDefault();

    this.setState({ errorMessage: "" });

    const email = this.state.email.trim();

    if (!email) {
      this.setState({ errorMessage: "Please enter your email first." });

      return;
    }

    if (!this.validateEmail(email)) {
      this.setState({ errorMessage: "Please enter a valid email address." });

      return;
    }

    const data = {
      session: {
        email,
      },
    };

    this.setState({ saving: true, email });

    postRequest("/session/password_resets", data)
      .then((response: AxiosResponse) => {
        this.setState({ emailSent: true, saving: false });
      })
      .catch((error: AxiosError) => {
        this.setState({ saving: false });
        console.log(error);
      });
  };

  render() {
    const { email, errorMessage, emailSent, saving } = this.state;
    const authState: AuthState = emailSent ? "sent" : "forgot";

    return (
      <div className="auth-page" id="forgot-password">
        <div className="auth-blob one" aria-hidden="true">
          <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#2E2437"
              d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
            />
            <path
              fill="#2E2437"
              d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
            />
          </svg>
        </div>
        <div className="auth-blob two" aria-hidden="true">
          <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#2E2437"
              d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
            />
            <path
              fill="#2E2437"
              d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
            />
          </svg>
        </div>

        <header className="auth-header">
          <a className="brand" href="/" aria-label="42courses home">
            <img
              src={require("../../../../assets/images/landings/home-logo.svg")}
              alt="42courses"
            />
          </a>
        </header>

        <main className="auth-main">
          <section
            className="signin-card"
            data-state={authState}
            aria-labelledby="forgot-password-title"
          >
            <div className="signin-head">
              <p className="signin-greeting">
                {emailSent ? "Check your inbox" : "No bother"}
              </p>
              <h1 className="signin-title" id="forgot-password-title">
                Forgot your password?
              </h1>
              <p className="signin-sub">
                {emailSent
                  ? "Check your inbox."
                  : "We will email you a link to set a new one."}
              </p>
            </div>

            {emailSent ? (
              <>
                <div className="email-tag">
                  <span className="email">{email}</span>
                </div>

                <div className="sent-confirmation">
                  <Link to="/" className="sent-tick" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  </Link>
                  <p className="sent-msg">
                    Reset link on its way. If it does not show up in a minute,
                    peek in your spam folder.
                  </p>
                </div>
              </>
            ) : (
              <form className="ui form" onSubmit={this.onSubmit} noValidate>
                <div className="field">
                  <div className="ui icon action input">
                    <label htmlFor="password_reset_email" className="sr-only">
                      Email
                    </label>
                    <input
                      type="email"
                      id="password_reset_email"
                      name="email"
                      placeholder="Email"
                      autoComplete="email"
                      onChange={this.handleChange}
                      value={email}
                      aria-invalid={Boolean(errorMessage)}
                      aria-describedby={
                        errorMessage ? "password_reset_email_error" : undefined
                      }
                    />
                  </div>
                  {errorMessage && (
                    <div
                      className="field-validation"
                      id="password_reset_email_error"
                      role="alert"
                    >
                      {errorMessage}
                    </div>
                  )}
                </div>
                <div className="action">
                  <button
                    type="submit"
                    className={`ui button ${saving ? "loading" : ""}`}
                    disabled={saving}
                  >
                    Send reset link
                  </button>
                </div>
              </form>
            )}

            <p className="signin-foot">
              <a href="/login">Back to sign in</a> &nbsp;&middot;&nbsp;{" "}
              <a href="/terms">Terms</a> &nbsp;&middot;&nbsp;{" "}
              <a href="/privacy">Privacy</a>
            </p>
          </section>
        </main>

        <footer className="auth-footer">
          Made with care in London. &nbsp;&middot;&nbsp;{" "}
          <a href="/">Back to 42courses</a>
        </footer>
      </div>
    );
  }
}

export default ForgotPassword;
